// Inspector-pane width helpers for the Discover page (b3).
// clampPaneWidth is pure (no DOM) so it's unit-testable; the storage helpers
// wrap localStorage with try/catch (private mode / disabled storage safe).

export const PANE_MIN_PX = 320
export const PANE_MAX_FRACTION = 0.6 // pane never exceeds 60% → list keeps ≥40%
export const PANE_DEFAULT_FRACTION = 0.34
const STORAGE_KEY = 'whl_pane_width'

// Clamp a desired width (px) to [PANE_MIN_PX, 60% of viewport]. Coerces
// NaN/negative/huge/corrupt values into range. viewportWidth must be > 0.
export function clampPaneWidth(px: number, viewportWidth: number): number {
  const max = Math.round(viewportWidth * PANE_MAX_FRACTION)
  // Guard a degenerate viewport (max could fall below the floor): never return
  // less than the floor.
  const ceil = Math.max(PANE_MIN_PX, max)
  if (!Number.isFinite(px)) return PANE_MIN_PX
  return Math.max(PANE_MIN_PX, Math.min(Math.round(px), ceil))
}

// The default width when nothing is stored: 34% of viewport, clamped.
export function defaultPaneWidth(viewportWidth: number): number {
  return clampPaneWidth(Math.round(viewportWidth * PANE_DEFAULT_FRACTION), viewportWidth)
}

// Read the stored width, clamped to the current viewport; falls back to the
// default when absent/invalid/unavailable.
export function readPaneWidth(viewportWidth: number): number {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw != null) {
      const n = Number(raw)
      if (Number.isFinite(n) && n > 0) return clampPaneWidth(n, viewportWidth)
    }
  } catch { /* storage unavailable — fall through to default */ }
  return defaultPaneWidth(viewportWidth)
}

// Persist a width (best-effort).
export function writePaneWidth(px: number): void {
  try { localStorage.setItem(STORAGE_KEY, String(px)) } catch { /* ignore */ }
}
